import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, KeyRound, PackageCheck, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import type {
  AdminBillingPlanSettingsInput,
  AdminBillingSettingsUpdateInput,
  BillingSettingsProvider
} from "@shudian/contracts";
import { adminGetBillingSettings, adminUpdateBillingSettings } from "../api/admin.js";
import { Alert } from "../components/ui/Alert.js";
import { Badge } from "../components/ui/Badge.js";
import { Button } from "../components/ui/Button.js";
import { Card } from "../components/ui/Card.js";
import { EntitlementChecklist } from "../components/EntitlementChecklist.js";
import { Field } from "../components/ui/Field.js";
import { Input } from "../components/ui/Input.js";
import { FullPageSpinner } from "../components/ui/Spinner.js";
import { MetricCard, PageHeader } from "../components/PageScaffold.js";
import { billingSettingsProviderLabel } from "../lib/billing.js";
import { billingPlanTemplate } from "../lib/entitlements.js";
import { CloudApiError, describeError } from "../lib/errors.js";
import { formatDateTime } from "../lib/format.js";

type FormState = {
  provider: BillingSettingsProvider;
  enabled: boolean;
  appId: string;
  gatewayUrl: string;
  notifyUrl: string;
  returnUrl: string;
  appCertSn: string;
  alipayRootCertSn: string;
  plans: BillingPlanFormState[];
};

type BillingPlanFormState = {
  planId: AdminBillingPlanSettingsInput["planId"];
  name: string;
  description: string;
  priceAmount: string;
  deviceLimit: string;
  storeLimit: string;
  entitlements: string[];
  features: string;
  renewalHint: string;
  active: boolean;
  sortOrder: string;
};

const DEFAULT_ALIPAY_GATEWAY = "https://openapi.alipay.com/gateway.do";
const PLAN_IDS: AdminBillingPlanSettingsInput["planId"][] = [
  "shudian_monthly",
  "shudian_quarterly",
  "shudian_yearly"
];

export function BillingSettingsPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>({
    provider: "disabled",
    enabled: false,
    appId: "",
    gatewayUrl: DEFAULT_ALIPAY_GATEWAY,
    notifyUrl: "",
    returnUrl: "",
    appCertSn: "",
    alipayRootCertSn: "",
    plans: []
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "billing-settings"],
    queryFn: () => adminGetBillingSettings()
  });

  useEffect(() => {
    if (!data?.settings) return;
    setForm({
      provider: data.settings.provider,
      enabled: data.settings.enabled,
      appId: data.settings.alipay?.appId ?? "",
      gatewayUrl: data.settings.alipay?.gatewayUrl ?? DEFAULT_ALIPAY_GATEWAY,
      notifyUrl: data.settings.alipay?.notifyUrl ?? "",
      returnUrl: data.settings.alipay?.returnUrl ?? "",
      appCertSn: data.settings.alipay?.appCertSn ?? "",
      alipayRootCertSn: data.settings.alipay?.alipayRootCertSn ?? "",
      plans: data.settings.plans.map(planToFormState)
    });
  }, [data]);

  const mutation = useMutation({
    mutationFn: (input: AdminBillingSettingsUpdateInput) => adminUpdateBillingSettings(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "billing-settings"] });
      setSuccessMessage("支付与套餐设置已保存");
    },
    onError: (err) => {
      setErrorMessage(err instanceof CloudApiError ? describeError(err.code) : "保存支付与套餐设置失败");
    }
  });

  if (isLoading) return <FullPageSpinner />;
  if (isError) {
    const message = error instanceof CloudApiError ? describeError(error.code) : "加载支付设置失败";
    return <Alert tone="danger">{message}</Alert>;
  }

  const settings = data?.settings;
  const alipaySecretsReady =
    settings?.alipaySecretStatus.appPrivateKeyConfigured &&
    settings.alipaySecretStatus.alipayPublicKeyConfigured;
  const formAlipayParamsReady = Boolean(
    form.appId.trim() &&
      form.gatewayUrl.trim() &&
      form.notifyUrl.trim()
  );
  const formAlipayCheckoutReady = Boolean(
    form.enabled &&
      form.provider === "alipay" &&
      formAlipayParamsReady &&
      alipaySecretsReady
  );
  const secretWarningVisible = form.enabled && form.provider === "alipay" && !alipaySecretsReady;
  const availablePlanIds = PLAN_IDS.filter(
    (planId) => !form.plans.some((plan) => plan.planId === planId)
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    const input = buildInput(form);
    const validationError = validateInput(input);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    mutation.mutate(input);
  };

  return (
    <div>
      <PageHeader
        eyebrow="订单与套餐 / 套餐与支付"
        title="套餐与支付"
        description="配置用户可购买套餐、支付渠道和支付宝接入参数。敏感密钥仍由 Cloud API 运行环境提供。"
      />

      <section className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-4">
        <MetricCard
          label="支付状态"
          value={settings?.enabled ? "已启用" : "已停用"}
          detail={billingSettingsProviderLabel(settings?.provider ?? "disabled")}
          tone={settings?.enabled ? "success" : "neutral"}
          icon={<CreditCard className="h-4 w-4" />}
        />
        <MetricCard
          label="支付宝密钥"
          value={alipaySecretsReady ? "已配置" : "未配置"}
          detail="应用私钥和支付宝公钥"
          tone={alipaySecretsReady ? "success" : "warning"}
          icon={<KeyRound className="h-4 w-4" />}
        />
        <MetricCard
          label="可购买套餐"
          value={form.plans.filter((plan) => plan.active).length}
          detail={`共 ${form.plans.length} 个套餐规格`}
          tone={form.plans.some((plan) => plan.active) ? "success" : "warning"}
          icon={<PackageCheck className="h-4 w-4" />}
        />
        <MetricCard label="更新时间" value={formatDateTime(settings?.updatedAt)} detail="最后保存支付设置" tone="neutral" />
      </section>

      <div className="space-y-6">
      <Card title="当前支付接入">
        <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <Detail
            label="支付状态"
            value={
              <Badge tone={settings?.enabled ? "success" : "neutral"}>
                {settings?.enabled ? "已启用" : "已停用"}
              </Badge>
            }
          />
          <Detail label="当前渠道" value={billingSettingsProviderLabel(settings?.provider ?? "disabled")} />
          <Detail label="更新时间" value={formatDateTime(settings?.updatedAt)} />
          <Detail
            label="支付宝密钥"
            value={
              <Badge tone={alipaySecretsReady ? "success" : "warning"}>
                {alipaySecretsReady ? "已配置" : "未配置"}
              </Badge>
            }
          />
        </dl>
      </Card>

      {form.provider === "alipay" && (
        <Card title="支付宝接入检查">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <ReadinessItem
              label="页面支付"
              ready={formAlipayCheckoutReady}
              readyText="可创建"
              pendingText="未就绪"
              detail={formAlipayCheckoutReady ? "订单会返回支付宝支付链接" : paymentReadinessHint(form, formAlipayParamsReady, Boolean(alipaySecretsReady))}
            />
            <ReadinessItem
              label="接入参数"
              ready={formAlipayParamsReady}
              readyText="完整"
              pendingText="待补齐"
              detail={form.appId.trim() ? "App ID 已填写" : "缺少 App ID"}
            />
            <ReadinessItem
              label="运行密钥"
              ready={Boolean(alipaySecretsReady)}
              readyText="已配置"
              pendingText="未配置"
              detail="应用私钥和支付宝公钥"
            />
            <ReadinessItem
              label="回调验签"
              ready={Boolean(settings?.alipaySecretStatus.alipayPublicKeyConfigured)}
              readyText="可验签"
              pendingText="未配置"
              detail="支付宝公钥"
            />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
            <Detail
              label="网关地址"
              value={
                <span className="block truncate font-mono text-xs" title={form.gatewayUrl}>
                  {form.gatewayUrl.trim() || "未填写"}
                </span>
              }
            />
            <Detail
              label="异步通知"
              value={
                <span className="block truncate font-mono text-xs" title={form.notifyUrl}>
                  {form.notifyUrl.trim() || "未填写"}
                </span>
              }
            />
            <Detail
              label="同步返回"
              value={
                <span className="block truncate font-mono text-xs" title={form.returnUrl}>
                  {form.returnUrl.trim() || "未填写"}
                </span>
              }
            />
          </div>
        </Card>
      )}

      <form className="space-y-6" onSubmit={submit} noValidate>
        {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}
        {successMessage && <Alert tone="success">{successMessage}</Alert>}

        <Card
          title="支付设置"
          description="后台只保存非敏感接入参数；支付宝应用私钥、支付宝公钥和回调密钥由 Cloud API 环境变量提供。"
        >
          <div className="space-y-5">
            {secretWarningVisible && (
              <Alert tone="warning">
                启用支付宝前必须在 Cloud API 运行环境配置支付宝应用私钥和支付宝公钥。
              </Alert>
            )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="支付渠道" htmlFor="billingProvider" required>
              <select
                id="billingProvider"
                className="h-9 rounded border border-surface-border bg-surface-panel px-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/70"
                value={form.provider}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    provider: event.target.value as BillingSettingsProvider,
                    enabled: event.target.value !== "disabled" && current.enabled
                  }))
                }
              >
                <option value="disabled">停用</option>
                <option value="simulated">模拟支付</option>
                <option value="alipay">支付宝</option>
              </select>
            </Field>
            <Field label="启用支付" htmlFor="billingEnabled">
              <label className="flex h-9 items-center gap-2 text-sm text-text-primary">
                <input
                  id="billingEnabled"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.enabled}
                  disabled={form.provider === "disabled"}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, enabled: event.target.checked }))
                  }
                />
                接受用户开通订单
              </label>
            </Field>
            <Field label="回调密钥" htmlFor="alipayWebhookSecretStatus">
              <div id="alipayWebhookSecretStatus" className="flex h-9 items-center">
                <Badge tone={settings?.alipaySecretStatus.webhookSecretConfigured ? "success" : "warning"}>
                  {settings?.alipaySecretStatus.webhookSecretConfigured ? "已配置" : "未配置"}
                </Badge>
              </div>
            </Field>
          </div>

          {form.provider === "alipay" && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="支付宝 App ID" htmlFor="alipayAppId" required>
                <Input
                  id="alipayAppId"
                  value={form.appId}
                  onChange={(event) => setForm((current) => ({ ...current, appId: event.target.value }))}
                />
              </Field>
              <Field label="支付宝网关" htmlFor="alipayGatewayUrl" required>
                <Input
                  id="alipayGatewayUrl"
                  value={form.gatewayUrl}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, gatewayUrl: event.target.value }))
                  }
                />
              </Field>
              <Field label="异步通知地址" htmlFor="alipayNotifyUrl" required>
                <Input
                  id="alipayNotifyUrl"
                  value={form.notifyUrl}
                  onChange={(event) => setForm((current) => ({ ...current, notifyUrl: event.target.value }))}
                />
              </Field>
              <Field label="同步返回地址" htmlFor="alipayReturnUrl">
                <Input
                  id="alipayReturnUrl"
                  value={form.returnUrl}
                  onChange={(event) => setForm((current) => ({ ...current, returnUrl: event.target.value }))}
                />
              </Field>
              <Field label="应用证书序列号" htmlFor="alipayAppCertSn">
                <Input
                  id="alipayAppCertSn"
                  value={form.appCertSn}
                  onChange={(event) => setForm((current) => ({ ...current, appCertSn: event.target.value }))}
                />
              </Field>
              <Field label="支付宝根证书序列号" htmlFor="alipayRootCertSn">
                <Input
                  id="alipayRootCertSn"
                  value={form.alipayRootCertSn}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, alipayRootCertSn: event.target.value }))
                  }
                />
              </Field>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
            <SecretStatus
              label="应用私钥"
              configured={settings?.alipaySecretStatus.appPrivateKeyConfigured ?? false}
              envName="SHUDIAN_ALIPAY_APP_PRIVATE_KEY"
            />
            <SecretStatus
              label="支付宝公钥"
              configured={settings?.alipaySecretStatus.alipayPublicKeyConfigured ?? false}
              envName="SHUDIAN_ALIPAY_PUBLIC_KEY"
            />
            <SecretStatus
              label="回调密钥"
              configured={settings?.alipaySecretStatus.webhookSecretConfigured ?? false}
              envName="SHUDIAN_ALIPAY_WEBHOOK_SECRET"
            />
          </div>

          </div>
        </Card>

        <Card
          title="套餐规格"
          description="套餐用于桌面端展示、创建订单和支付成功后的授权开通；常用文案和能力已按运营场景预置。"
          actions={
            <div className="flex flex-wrap justify-end gap-2">
              {availablePlanIds.map((planId) => (
                <Button
                  key={planId}
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => addPlanForm(planId)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  添加{billingPlanLabel(planId)}
                </Button>
              ))}
            </div>
          }
        >
          <div className="space-y-4">
            {form.plans.length === 0 ? (
              <div className="rounded border border-dashed border-surface-border bg-surface-raised px-4 py-6 text-sm text-text-secondary">
                云端暂未配置套餐。请添加套餐规格，系统会自动带出常用授权能力、简介和展示亮点。
              </div>
            ) : null}
            {form.plans.map((plan, index) => (
              <div
                key={plan.planId}
                className="rounded border border-surface-border bg-surface-raised p-4"
              >
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-text-primary">
                      {billingPlanLabel(plan.planId)}
                    </h3>
                    <p className="mt-0.5 font-mono text-xs text-text-muted">{plan.planId}</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-text-primary">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={plan.active}
                      onChange={(event) => updatePlanForm(index, { active: event.target.checked })}
                    />
                    允许购买
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => removePlanForm(index)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    删除
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <Field label="套餐名称" htmlFor={`${plan.planId}-name`} required>
                    <Input
                      id={`${plan.planId}-name`}
                      value={plan.name}
                      onChange={(event) => updatePlanForm(index, { name: event.target.value })}
                    />
                  </Field>
                  <Field label="价格（分）" htmlFor={`${plan.planId}-price`} required>
                    <Input
                      id={`${plan.planId}-price`}
                      type="number"
                      min={0}
                      step={1}
                      value={plan.priceAmount}
                      onChange={(event) => updatePlanForm(index, { priceAmount: event.target.value })}
                    />
                  </Field>
                  <Field label="设备数" htmlFor={`${plan.planId}-devices`} required>
                    <Input
                      id={`${plan.planId}-devices`}
                      type="number"
                      min={1}
                      max={50}
                      step={1}
                      value={plan.deviceLimit}
                      onChange={(event) => updatePlanForm(index, { deviceLimit: event.target.value })}
                    />
                  </Field>
                  <Field label="店铺数" htmlFor={`${plan.planId}-stores`} required>
                    <Input
                      id={`${plan.planId}-stores`}
                      type="number"
                      min={0}
                      max={10000}
                      step={10}
                      value={plan.storeLimit}
                      onChange={(event) => updatePlanForm(index, { storeLimit: event.target.value })}
                    />
                  </Field>
                  <Field label="排序" htmlFor={`${plan.planId}-sort`} required>
                    <Input
                      id={`${plan.planId}-sort`}
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={plan.sortOrder}
                      onChange={(event) => updatePlanForm(index, { sortOrder: event.target.value })}
                    />
                  </Field>
                  <Field label="续费提示" htmlFor={`${plan.planId}-renewal`} required>
                    <Input
                      id={`${plan.planId}-renewal`}
                      value={plan.renewalHint}
                      onChange={(event) => updatePlanForm(index, { renewalHint: event.target.value })}
                    />
                  </Field>
                  <Field label="简介" htmlFor={`${plan.planId}-description`} required className="md:col-span-1">
                    <Input
                      id={`${plan.planId}-description`}
                      value={plan.description}
                      onChange={(event) => updatePlanForm(index, { description: event.target.value })}
                    />
                  </Field>
                  <Field
                    label="授权能力"
                    required
                    className="md:col-span-2"
                    hint="运营只需勾选中文能力；系统会保存为桌面端识别的内部权限码。"
                  >
                    <EntitlementChecklist
                      value={plan.entitlements}
                      onChange={(entitlements) => updatePlanForm(index, { entitlements })}
                    />
                  </Field>
                  <Field label="桌面端展示亮点" htmlFor={`${plan.planId}-features`} required className="md:col-span-2">
                    <textarea
                      id={`${plan.planId}-features`}
                      className="min-h-28 rounded border border-surface-border bg-surface-panel px-2.5 py-2 text-sm text-text-primary focus:border-accent/70 focus:outline-none focus:ring-2 focus:ring-accent/40"
                      value={plan.features}
                      onChange={(event) => updatePlanForm(index, { features: event.target.value })}
                    />
                  </Field>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => updatePlanForm(index, planInputToFormState(billingPlanTemplate(plan.planId)))}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    恢复推荐配置
                  </Button>
                  <span className="text-xs text-text-muted">
                    推荐配置会重置名称、简介、价格、额度、能力、亮点和排序。
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending}>
            <Save className="h-3.5 w-3.5" />
            {mutation.isPending ? "保存中..." : "保存支付与套餐设置"}
          </Button>
        </div>
      </form>
      </div>
    </div>
  );

  function updatePlanForm(index: number, patch: Partial<BillingPlanFormState>) {
    setForm((current) => ({
      ...current,
      plans: current.plans.map((plan, planIndex) =>
        planIndex === index ? { ...plan, ...patch } : plan
      )
    }));
  }

  function addPlanForm(planId: AdminBillingPlanSettingsInput["planId"]) {
    setForm((current) => ({
      ...current,
      plans: [...current.plans, blankPlanFormState(planId)]
    }));
  }

  function removePlanForm(index: number) {
    setForm((current) => ({
      ...current,
      plans: current.plans.filter((_, planIndex) => planIndex !== index)
    }));
  }
}

function buildInput(form: FormState): AdminBillingSettingsUpdateInput {
  return {
    provider: form.enabled ? form.provider : "disabled",
    enabled: form.enabled,
    plans: form.plans.map(formStateToPlanInput),
    alipay:
      form.provider === "alipay"
        ? {
            appId: form.appId.trim(),
            gatewayUrl: form.gatewayUrl.trim(),
            notifyUrl: form.notifyUrl.trim(),
            returnUrl: optionalValue(form.returnUrl),
            appCertSn: optionalValue(form.appCertSn),
            alipayRootCertSn: optionalValue(form.alipayRootCertSn)
          }
        : undefined
  };
}

function validateInput(input: AdminBillingSettingsUpdateInput): string | null {
  const planError = validatePlanInputs(input.plans ?? []);
  if (planError) return planError;
  if (!input.enabled) return null;
  if (!input.plans || input.plans.length === 0 || !input.plans.some((plan) => plan.active)) {
    return "请先配置至少一个可购买套餐";
  }
  if (input.provider === "disabled") return "请选择支付渠道";
  if (input.provider === "alipay") {
    if (!input.alipay?.appId) return "请填写支付宝 App ID";
    if (!input.alipay.gatewayUrl) return "请填写支付宝网关";
    if (!input.alipay.notifyUrl) return "请填写异步通知地址";
  }
  return null;
}

function validatePlanInputs(plans: AdminBillingPlanSettingsInput[]): string | null {
  for (const plan of plans) {
    if (!plan.name) return `${billingPlanLabel(plan.planId)} 需要填写套餐名称`;
    if (!plan.description) return `${billingPlanLabel(plan.planId)} 需要填写简介`;
    if (plan.priceAmount < 0) return `${billingPlanLabel(plan.planId)} 价格不能小于 0`;
    if (plan.deviceLimit < 1) return `${billingPlanLabel(plan.planId)} 设备数不能小于 1`;
    if (plan.storeLimit < 0) return `${billingPlanLabel(plan.planId)} 店铺数不能小于 0`;
    if (plan.entitlements.length === 0) return `${billingPlanLabel(plan.planId)} 需要配置授权能力`;
    if (plan.features.length === 0) return `${billingPlanLabel(plan.planId)} 需要配置展示亮点`;
  }
  return null;
}

function paymentReadinessHint(
  form: FormState,
  paramsReady: boolean,
  secretsReady: boolean
): string {
  if (!form.enabled) return "需要勾选启用支付";
  if (!paramsReady) return "需要填写 App ID、网关和异步通知地址";
  if (!secretsReady) return "需要配置应用私钥和支付宝公钥";
  return "检查支付渠道设置";
}

function optionalValue(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function planToFormState(plan: {
  planId: AdminBillingPlanSettingsInput["planId"];
  name: string;
  description: string;
  priceAmount: number;
  quotaLimits: Array<{ usageType: string; limit: number }>;
  entitlements: string[];
  deviceLimit: number;
  storeLimit: number;
  active: boolean;
  features: string[];
  renewalHint: string;
  sortOrder: number;
}): BillingPlanFormState {
  return planInputToFormState({
    planId: plan.planId,
    name: plan.name,
    description: plan.description,
    priceAmount: plan.priceAmount,
    quotaLimits: plan.quotaLimits.map((quota) => ({
      usageType: quota.usageType,
      limit: quota.limit
    })),
    entitlements: plan.entitlements,
    deviceLimit: plan.deviceLimit,
    storeLimit: plan.storeLimit,
    active: plan.active,
    features: plan.features,
    renewalHint: plan.renewalHint,
    sortOrder: plan.sortOrder
  });
}

function planInputToFormState(plan: AdminBillingPlanSettingsInput): BillingPlanFormState {
  return {
    planId: plan.planId,
    name: plan.name,
    description: plan.description,
    priceAmount: String(plan.priceAmount),
    deviceLimit: String(plan.deviceLimit),
    storeLimit: String(plan.storeLimit),
    entitlements: [...plan.entitlements],
    features: plan.features.join("\n"),
    renewalHint: plan.renewalHint,
    active: plan.active,
    sortOrder: String(plan.sortOrder)
  };
}

function blankPlanFormState(planId: AdminBillingPlanSettingsInput["planId"]): BillingPlanFormState {
  return planInputToFormState(billingPlanTemplate(planId));
}

function formStateToPlanInput(plan: BillingPlanFormState): AdminBillingPlanSettingsInput {
  return {
    planId: plan.planId,
    name: plan.name.trim(),
    description: plan.description.trim(),
    priceAmount: parseIntegerInput(plan.priceAmount),
    quotaLimits: [],
    entitlements: [...plan.entitlements],
    deviceLimit: parseIntegerInput(plan.deviceLimit),
    storeLimit: parseIntegerInput(plan.storeLimit),
    active: plan.active,
    features: splitLines(plan.features),
    renewalHint: plan.renewalHint.trim(),
    sortOrder: parseIntegerInput(plan.sortOrder)
  };
}

function splitLines(value: string): string[] {
  return value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseIntegerInput(value: string): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : -1;
}

function billingPlanLabel(planId: AdminBillingPlanSettingsInput["planId"]): string {
  if (planId === "shudian_monthly") return "月套餐";
  if (planId === "shudian_quarterly") return "季套餐";
  return "年套餐";
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-text-primary">{value}</dd>
    </div>
  );
}

function ReadinessItem({
  label,
  ready,
  readyText,
  pendingText,
  detail
}: {
  label: string;
  ready: boolean;
  readyText: string;
  pendingText: string;
  detail: string;
}) {
  return (
    <div className="rounded border border-surface-border bg-surface-raised px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-text-primary">{label}</div>
        <Badge tone={ready ? "success" : "warning"}>{ready ? readyText : pendingText}</Badge>
      </div>
      <div className="mt-1 text-xs text-text-muted">{detail}</div>
    </div>
  );
}

function SecretStatus({
  label,
  configured,
  envName
}: {
  label: string;
  configured: boolean;
  envName: string;
}) {
  return (
    <div className="rounded border border-surface-border bg-surface-raised px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-text-primary">{label}</span>
        <Badge tone={configured ? "success" : "warning"}>{configured ? "已配置" : "未配置"}</Badge>
      </div>
      <div className="mt-1 font-mono text-xs text-text-muted">{envName}</div>
    </div>
  );
}
